/**
 * FilterSection Component
 * 사용자 검색 필터 영역
 */
import React, { useCallback } from 'react';
import {
    FilterState,
    FILTER_LABELS,
    FILTER_PLACEHOLDERS,
    BUTTON_LABELS,
    USE_YN_OPTIONS,
    USER_TYPE_OPTIONS,
} from './userManagement.helpers';

import '@/shared/styles/search-form.css';

// ======== 타입 ========

interface FilterSectionProps {
    filters: FilterState;
    onFilterChange: (filters: FilterState) => void;
    onSearch: () => void;
    onNewUser: () => void;
    userTypeOptions?: readonly { value: string; label: string }[];
}

// ======== 컴포넌트 ========

const FilterSection: React.FC<FilterSectionProps> = ({
                                                         filters,
                                                         onFilterChange,
                                                         onSearch,
                                                         onNewUser,
                                                         userTypeOptions = USER_TYPE_OPTIONS,
                                                     }) => {
    /**
     * 텍스트 필드 변경 핸들러
     */
    const handleTextChange = useCallback((field: keyof FilterState) => (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        onFilterChange({ ...filters, [field]: e.target.value });
    }, [filters, onFilterChange]);

    /**
     * 셀렉트 필드 변경 핸들러
     */
    const handleSelectChange = useCallback((field: keyof FilterState) => (
        e: React.ChangeEvent<HTMLSelectElement>
    ) => {
        onFilterChange({ ...filters, [field]: e.target.value });
    }, [filters, onFilterChange]);

    /**
     * 엔터키 검색 핸들러
     */
    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter') onSearch();
    }, [onSearch]);

    return (
        <div className="card search-card">
            <div className="card-body">
                <div className="search-form user-management-search-form">
                    {/* 이름/ID 통합 검색 */}
                    <div className="form-group">
                        <div className="form-field">
                            <label className="form-label">
                                <i className="fas fa-search" />
                                {FILTER_LABELS.KEYWORD}
                            </label>
                            <input
                                type="text"
                                className="text-input"
                                placeholder={FILTER_PLACEHOLDERS.KEYWORD}
                                value={filters.keyword}
                                onChange={handleTextChange('keyword')}
                                onKeyDown={handleKeyDown}
                            />
                        </div>
                    </div>

                    {/* 협력사명 */}
                    <div className="form-group">
                        <div className="form-field">
                            <label className="form-label">
                                <i className="fas fa-building" />
                                {FILTER_LABELS.SUPP_NAME}
                            </label>
                            <input
                                type="text"
                                className="text-input"
                                placeholder={FILTER_PLACEHOLDERS.SUPP_NAME}
                                value={filters.suppName}
                                onChange={handleTextChange('suppName')}
                                onKeyDown={handleKeyDown}
                            />
                        </div>
                    </div>

                    {/* 사용자 타입 */}
                    <div className="form-group">
                        <div className="form-field">
                            <label className="form-label">
                                <i className="fas fa-shield-alt" />
                                {FILTER_LABELS.USER_TYPE}
                            </label>
                            <div className="status-select-wrapper">
                                <select
                                    className="status-select"
                                    value={filters.userType}
                                    onChange={handleSelectChange('userType')}
                                >
                                    {userTypeOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* 사용 여부 */}
                    <div className="form-group">
                        <div className="form-field">
                            <label className="form-label">
                                <i className="fas fa-toggle-on" />
                                {FILTER_LABELS.USE_YN}
                            </label>
                            <div className="status-select-wrapper">
                                <select
                                    className="status-select"
                                    value={filters.useYn}
                                    onChange={handleSelectChange('useYn')}
                                >
                                    {USE_YN_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* 버튼 그룹 */}
                    <div className="form-group button-group">
                        <button className="btn btn-secondary" onClick={onNewUser}>
                            <i className="fas fa-plus" />
                            {BUTTON_LABELS.NEW}
                        </button>
                        <button className="btn btn-primary" onClick={onSearch}>
                            <i className="fas fa-search" />
                            {BUTTON_LABELS.SEARCH}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FilterSection;
